import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    UpdatePorschePaymentModuleDocument,
    type UpdatePorschePaymentModuleMutation,
    type UpdatePorschePaymentModuleMutationVariables,
} from '../../../../api/mutations/updatePorschePaymentModule';
import { type PorschePaymentModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = PorschePaymentModuleSettings;

type UpdatePorschePaymentModuleFormProps = {
    moduleId: string;
    initialValues: FormValues;
    onComplete: () => void;
};

const loadingMessageKey = 'update-porsche-payment-module-loading';

const UpdatePorschePaymentModuleForm = ({
    moduleId,
    initialValues,
    onComplete,
}: UpdatePorschePaymentModuleFormProps) => {
    const { t } = useTranslation(['porschePaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('porschePaymentModuleDetails:paymentModuleDrawer.messages.submitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdatePorschePaymentModuleMutation, UpdatePorschePaymentModuleMutationVariables>({
                    mutation: UpdatePorschePaymentModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('porschePaymentModuleDetails:paymentModuleDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updatePorschePaymentModule"
                    layout="vertical"
                    name="updatePorschePaymentModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentModuleDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdatePorschePaymentModuleForm;
